import type { CasesBreakdown, CasesBreakdownEntry } from '@compass/client/types/cases_analytics';
import React, { useMemo, useRef } from 'react';

import { Card } from '@/components/ui/card';
import {
  type ChartDownloadFormat,
  ChartDownloadMenu,
} from '@/pages/analytics/components/ChartDownloadMenu';
import { ChartRenderBoundary } from '@/pages/analytics/components/ChartRenderBoundary';
import { ChartBodySkeleton, ChartEmptyState } from '@/pages/analytics/components/ChartStates';
import { DonutBreakdown, formatShare } from '@/pages/analytics/components/DonutBreakdown';
import type { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import { normalizeCaseBreakdown } from '@/pages/analytics/utils/normalizeChartRows';
import { topNWithOther } from '@/pages/analytics/utils/series';

const GRID_CLASS = 'grid grid-cols-1 gap-3 md:grid-cols-3';
const CARD_CLASS = 'h-full w-full p-4 shadow-sm';

interface CaseBreakdownSectionProps {
  breakdown?: CasesBreakdown;
  colors: Map<string, string>;
  download: ReturnType<typeof useDownloadChart>;
  loading?: boolean;
}

const SECTIONS: { key: 'by_program' | 'by_submission_method' | 'by_report_type'; title: string }[] =
  [
    { key: 'by_program', title: 'By Program' },
    { key: 'by_submission_method', title: 'By Submission Method' },
    { key: 'by_report_type', title: 'By Report Type' },
  ];

const BreakdownDonut: React.FC<{
  title: string;
  entries: CasesBreakdownEntry[];
  total: number;
  colors: Map<string, string>;
}> = ({ title, entries, total, colors }) => {
  // Largest first, never folded — the submitted-over-time chart folds instead.
  const slices = useMemo(
    () =>
      topNWithOther(
        entries.map(e => ({
          group: e.group ?? '',
          group_name: e.group_name,
          case_count: e.case_count,
        })),
        Infinity,
      ),
    [entries],
  );
  // The donut draws the sorted slices, so its order matches this list.
  const rows = useMemo(
    () =>
      slices.map(s => ({
        label: s.name,
        callCount: s.value,
        value: s.key,
        color: colors.get(s.key),
      })),
    [slices, colors],
  );

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-semibold leading-5 text-foreground">{title}</h4>
      {entries.length === 0 ? (
        <ChartEmptyState height={144} />
      ) : (
        <>
          <DonutBreakdown
            rows={rows}
            totalCalls={total}
            title={title}
            unit="cases"
            rawLabels
            center={{ primary: total.toLocaleString('en-US'), secondary: 'cases' }}
          />
          <ul className="divide-y">
            {slices.map(slice => (
              <li key={slice.key} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: colors.get(slice.key) }}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-5 text-foreground">
                  {slice.name}
                </span>
                <span className="shrink-0 text-[13px] text-muted-foreground tabular-nums">
                  {formatShare(slice.value, total)}
                </span>
                <span className="shrink-0 text-[13px] font-semibold leading-5 text-foreground tabular-nums">
                  {slice.value.toLocaleString('en-US')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export const CaseBreakdownSection: React.FC<CaseBreakdownSectionProps> = ({
  breakdown,
  colors,
  download,
  loading,
}) => {
  const total = breakdown?.total ?? 0;
  const exportRef = useRef<HTMLElement>(null);
  const handleDownload = (format: ChartDownloadFormat) =>
    breakdown &&
    download(normalizeCaseBreakdown(breakdown), format, 'Case breakdown', exportRef.current);

  return (
    <section className="space-y-4" data-export-pdf-block ref={exportRef}>
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold leading-5 text-foreground">Case breakdown</h3>
          <p className="text-xs leading-5 text-muted-foreground">How the cases split up</p>
        </div>
        <span data-export-hidden>
          <ChartDownloadMenu onDownload={handleDownload} disabled={!breakdown} />
        </span>
      </header>
      {loading ? (
        <div className={GRID_CLASS}>
          {SECTIONS.map(section => (
            <Card key={section.key} className={CARD_CLASS}>
              <ChartBodySkeleton height={180} />
            </Card>
          ))}
        </div>
      ) : (
        <ChartRenderBoundary resetKeys={[breakdown]} height={180}>
          {total === 0 ? (
            <ChartEmptyState height={180} />
          ) : (
            <div className={GRID_CLASS}>
              {SECTIONS.map(section => (
                <Card
                  key={section.key}
                  data-breakdown-card
                  className={CARD_CLASS}
                  data-export-pdf-block
                >
                  <BreakdownDonut
                    title={section.title}
                    entries={breakdown?.[section.key] ?? []}
                    total={total}
                    colors={colors}
                  />
                </Card>
              ))}
            </div>
          )}
        </ChartRenderBoundary>
      )}
    </section>
  );
};
